import { devWarnMissingAccessibleNameOnce } from '@/lib/a11y/accessibilityWarnings'
import { twMerge } from '@/lib/classname/twMerge'

export type NotificationProps = UiComponentProps<{
  /** Optional number to display. If not a valid number, displays a dot. */
  number?: number
}>

/**
 * Renders an inline notification badge that can either display a number or a small dot.
 *
 * Accessibility:
 * - By default, the badge is treated as decorative (`role="presentation"`, `aria-hidden="true"`).
 * - To expose it to assistive technologies, provide a meaningful `aria-label` that explains the
 *   purpose of the badge (not just the raw number).
 *   Example: `<Notification number={8} aria-label="You have 8 quotes waiting for validation" />`.
 */
export function Notification({
  number,
  className,
  id,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: NotificationProps) {
  devWarnMissingAccessibleNameOnce({
    componentName: 'Notification',
    ariaLabel,
    ariaHidden: !ariaLabel,
  })

  const isDot = number === undefined

  return (
    <span
      id={id}
      role={ariaLabel ? 'status' : 'presentation'}
      aria-label={ariaLabel}
      aria-hidden={!ariaLabel}
      className={twMerge(
        'relative flex shrink-0 items-center justify-center rounded-full bg-primary text-center font-bold text-white',
        isDot ? 'size-[0.75rem]' : 'size-2 text-xs leading-compact',
        className,
      )}
      data-testid={dataTestId ?? 'notification'}
    >
      {!isDot && number}
    </span>
  )
}
